import imgixLoader from './imageLoader';

const RAW_ASSET_PREFIX =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ASSET_PREFIX) || '';

const ASSET_PREFIX =
  RAW_ASSET_PREFIX === '/' || RAW_ASSET_PREFIX === ''
    ? ''
    : RAW_ASSET_PREFIX.startsWith('/')
      ? RAW_ASSET_PREFIX.replace(/\/$/, '')
      : `/${RAW_ASSET_PREFIX.replace(/\/$/, '')}`;

const DEFAULT_FALLBACK_IMAGE = `${ASSET_PREFIX}/images/default-campsite-hero.jpg`;

/**
 * Normalise image source values so they work with the custom Imgix loader and
 * Shopify proxy paths. Handles fully-qualified URLs, protocol-relative sources,
 * Imgix-specific domains and relative asset paths.
 */
export function normalizeImageSrc(rawSrc?: string, fallback: string = DEFAULT_FALLBACK_IMAGE): string {
  if (!rawSrc) {
    return fallback;
  }

  const trimmed = rawSrc.trim();

  if (!trimmed) {
    return fallback;
  }

  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.toLowerCase().includes('imgix.net')) {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `https://${trimmed.replace(/^\/\//, '')}`;
  }

  const proxyPrefix = '/apps/campsite-explorer';
  let normalized = trimmed;

  if (normalized.startsWith(proxyPrefix)) {
    normalized = normalized.slice(proxyPrefix.length);
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  normalized = normalized.replace(/\/{2,}/g, '/');

  return normalized || fallback;
}

export { DEFAULT_FALLBACK_IMAGE };

/**
 * Convert an image source into a fully-qualified Imgix URL (or fallback).
 */
export function toOptimizedImageSrc(
  rawSrc?: string,
  options: { width?: number; quality?: number } = {}
): string {
  const { width = 800, quality = 75 } = options;
  const normalized = normalizeImageSrc(rawSrc, DEFAULT_FALLBACK_IMAGE);
  const baseUrl =
    process.env.NEXT_PUBLIC_IMGIX_URL || 'https://jola-888.imgix.net';

  if (!normalized || normalized === DEFAULT_FALLBACK_IMAGE) {
    return DEFAULT_FALLBACK_IMAGE;
  }

  // If the source already points at our Imgix domain, just tweak the params.
  if (normalized.startsWith(baseUrl)) {
    try {
      const url = new URL(normalized);
      url.searchParams.set('auto', 'format,compress');
      url.searchParams.set('fit', 'crop');
      url.searchParams.set('crop', 'entropy,edges');
      url.searchParams.set('w', String(width));
      url.searchParams.set('q', String(quality));
      return url.toString();
    } catch (error) {
      console.warn('toOptimizedImageSrc: failed to adjust Imgix URL params', {
        rawSrc,
        normalized,
        error,
      });
      return normalized;
    }
  }

  try {
    return imgixLoader({
      src: normalized,
      width,
      quality,
    });
  } catch (error) {
    console.warn('toOptimizedImageSrc: failed to build Imgix URL, using normalized path', {
      rawSrc,
      normalized,
      error,
    });
    return normalized;
  }
}
